import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { safeRemoveItem } from '../utils/safeStorage';
import { runDesignLogoutClearer } from '../utils/designLogoutHook';

interface User {
  id: string;
  email: string;
  name?: string;
  company?: string;
  phone?: string;
  cognitoId?: string;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  idToken?: string;
}

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  sessionId: string;
  mfaRequired: boolean;
  mfaSession?: string;
  
  // Actions
  setUser: (user: User | null) => void;
  setTokens: (tokens: AuthTokens | null) => void;
  login: (user: User, tokens: AuthTokens) => void;
  logout: () => void;
  setMfaRequired: (required: boolean, session?: string) => void;
  getAccessToken: () => string | null;
}

// Generate a session ID for guest users using a CSPRNG (GH#110).
// Math.random() is predictable and must not be used for session identifiers.
const generateSessionId = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return 'sess_' + Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * #831 — tokens are memory-only (GH#123), but `isAuthenticated`/`user` persist to
 * localStorage. After a reload the persisted flags rehydrate while `tokens` is
 * null, so the UI would render as logged-in while every authenticated request
 * 401s (a zombie session). Reconcile the rehydrated state: an authenticated flag
 * with no tokens is stale → present as logged-out so the UI is honest and the
 * user can re-login cleanly. A genuine session (tokens present) is untouched, and
 * no token is ever written to localStorage (GH#123 held). The real refresh-token
 * flow is the separate #379 decision.
 */
export function reconcileRehydratedAuth(state: {
  isAuthenticated: boolean;
  tokens: AuthTokens | null;
  user: User | null;
}): { isAuthenticated: boolean; user: User | null } {
  if (state.isAuthenticated && !state.tokens) {
    return { isAuthenticated: false, user: null };
  }
  return { isAuthenticated: state.isAuthenticated, user: state.user };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      sessionId: generateSessionId(),
      mfaRequired: false,
      mfaSession: undefined,
      
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      
      setTokens: (tokens) => set({ tokens }),
      
      login: (user, tokens) => set({ 
        user, 
        tokens,
        isAuthenticated: true,
        mfaRequired: false,
        mfaSession: undefined,
      }),
      
      logout: () => {
        // #988: clear canvas only if the editor chunk already registered a
        // clearer. Do NOT dynamic-import designStore here — Vite would
        // modulepreload vendor-fabric (~300KB) on every marketing page.
        runDesignLogoutClearer();

        // GH#123: Remove stale legacy auth keys that may have persisted tokens
        // from before this fix. The primary key is handled by Zustand's persist
        // middleware; the legacy keys must be cleaned up explicitly.
        safeRemoveItem('tbl-foam-auth');
        safeRemoveItem('psi-foam-auth');

        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          sessionId: generateSessionId(),
          mfaRequired: false,
          mfaSession: undefined,
        });
      },
      
      setMfaRequired: (required, session) => set({
        mfaRequired: required,
        mfaSession: session,
      }),
      
      getAccessToken: () => {
        const state = get();
        return state.tokens?.accessToken || null;
      },
    }),
    {
      name: 'foam-casebuilder-auth',
      // GH#123: tokens (accessToken, refreshToken, idToken) are intentionally
      // excluded from persisted state. Storing JWTs in localStorage exposes
      // them to any XSS payload. Tokens are kept in memory only; on page
      // refresh the user re-authenticates via the Cognito hosted UI.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        sessionId: state.sessionId,
      }),
      // #831: after rehydration the persisted isAuthenticated/user come back but
      // tokens are memory-only (never persisted), so clear a tokenless "logged in"
      // state instead of leaving the UI lying about a session that will 401.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const reconciled = reconcileRehydratedAuth(state);
        state.isAuthenticated = reconciled.isAuthenticated;
        state.user = reconciled.user;
      },
    }
  )
);
